import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PreferenceChip, type PreferenceChipVariant } from "@/components/profile/PreferenceChip";
import { X, Plus } from "lucide-react";

export interface TagListEditorProps {
  label?: string;
  items: string[];
  inputValue: string;
  onInputChange: (value: string) => void;
  onAdd: (raw: string) => void;
  onEdit: (value: string, index: number) => void;
  onRemove: (index: number) => void;
  placeholder?: string;
  id?: string;
  /** Меньшие отступы (для страницы редактирования профиля) */
  compact?: boolean;
  /** Единый стиль: AddRow (поле + круглая кнопка), чипы rounded-full, helper 12 muted */
  unified?: boolean;
  /** Вариант чипа: allergy / like / dislike — единый стиль create & edit, смысловые цвета */
  chipVariant?: PreferenceChipVariant;
  /** @deprecated Используйте chipVariant для профиля; оставлено для совместимости */
  variant?: "default" | "pill";
  /** Подсказка под полем добавления (только при unified), например "запятая или Enter" */
  helperText?: string;
  /** Только чтение: input disabled/readOnly (для Free — тап по секции обрабатывается обёрткой → paywall) */
  readOnly?: boolean;
}

const chipBase =
  "inline-flex items-center gap-1.5 h-8 rounded-full px-3 text-[13px] font-normal bg-primary-light/80 text-foreground border-0 cursor-pointer hover:bg-primary-light transition-colors";

const profileLabelClass = "text-sm font-medium";
const profileAddRowClass = "flex h-11 items-center gap-3 px-4 rounded-xl border border-input bg-background hover:border-primary/30 transition-colors cursor-text w-full";
const profileAddBtnClass =
  "w-5 h-5 rounded-full flex items-center justify-center shrink-0 bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50";
const profileHelperClass = "text-xs text-muted-foreground mt-1.5";
const profileInputClass = "flex-1 min-w-0 border-0 bg-transparent py-2 text-[15px] font-medium text-foreground focus:outline-none focus:ring-0 placeholder:text-muted-foreground";

export function TagListEditor({
  label,
  items,
  inputValue,
  onInputChange,
  onAdd,
  onEdit,
  onRemove,
  placeholder = "Добавить (запятая или Enter)",
  id,
  compact = false,
  unified = false,
  chipVariant,
  variant = "default",
  helperText,
  readOnly = false,
}: TagListEditorProps) {
  const inputId = id ?? `tag-list-${(label ?? "tag").replace(/\s+/g, "-").toLowerCase()}`;
  const spaceClass = compact ? "space-y-1.5" : "space-y-2";
  const chipsMb = compact ? "mb-1" : "mb-2";
  const chipsGap = "gap-2";
  const useProfileChips = Boolean(chipVariant);
  const isPill = variant === "pill" || useProfileChips;
  const chipClassName = !useProfileChips && isPill ? "profile-pill profile-tag-enter" : chipBase;
  const addRowClass = isPill ? profileAddRowClass : "flex h-12 items-center gap-3 px-4 rounded-2xl border border-primary-border/60 bg-white hover:bg-muted/30 transition-colors cursor-text w-full";
  const addBtnClass = isPill ? profileAddBtnClass : "w-9 h-9 rounded-full bg-primary flex items-center justify-center shrink-0 text-primary-foreground hover:opacity-90 disabled:opacity-50";

  if (unified || useProfileChips) {
    return (
      <div className={useProfileChips ? "space-y-1.5" : "space-y-2"}>
        {label ? (
          <Label htmlFor={inputId} className={useProfileChips ? profileLabelClass : isPill ? "profile-label font-medium text-foreground" : "text-sm font-semibold text-foreground"}>
            {label}
          </Label>
        ) : null}
        {items.length > 0 && (
          <div className={`flex flex-wrap ${chipsGap}`}>
            {items.map((item, i) =>
              useProfileChips && chipVariant ? (
                <span
                  key={i}
                  role="button"
                  tabIndex={0}
                  onClick={() => onEdit(item, i)}
                  onKeyDown={(e) => e.key === "Enter" && onEdit(item, i)}
                  className="profile-tag-enter"
                >
                  <PreferenceChip
                    label={item}
                    variant={chipVariant}
                    size="compact"
                    allowWrap
                    removable
                    onRemove={() => onRemove(i)}
                  />
                </span>
              ) : (
                <div
                  key={i}
                  role="button"
                  tabIndex={0}
                  onClick={() => onEdit(item, i)}
                  onKeyDown={(e) => e.key === "Enter" && onEdit(item, i)}
                  className={chipClassName}
                  style={
                    isPill && !useProfileChips
                      ? {
                          background: "var(--primary-pill-surface)",
                          color: "var(--primary)",
                        }
                      : undefined
                  }
                >
                  <span className="truncate max-w-[140px]">{item}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(i);
                    }}
                    className={
                      isPill
                        ? "w-5 h-5 rounded-full flex items-center justify-center hover:bg-primary/15 shrink-0 -mr-0.5"
                        : "w-7 h-7 rounded-full flex items-center justify-center hover:bg-primary-light shrink-0 -mr-0.5"
                    }
                    aria-label="Удалить"
                  >
                    <X className={isPill ? "w-3 h-3 text-primary" : "w-3.5 h-3.5 text-muted-foreground"} />
                  </button>
                </div>
              )
            )}
          </div>
        )}
        <form
          className={addRowClass}
          onSubmit={(e) => {
            e.preventDefault();
            if (!readOnly && inputValue.trim()) onAdd(inputValue);
          }}
          noValidate
        >
          <button
            type="button"
            className={addBtnClass}
            onClick={(e) => {
              e.preventDefault();
              if (!readOnly && inputValue.trim()) onAdd(inputValue);
            }}
            disabled={readOnly || !inputValue.trim()}
            aria-label="Добавить"
          >
            <Plus className={useProfileChips ? "w-5 h-5" : isPill ? "w-4 h-4" : "w-5 h-5"} />
          </button>
          <input
            id={inputId}
            type="text"
            autoComplete="off"
            value={inputValue}
            readOnly={readOnly}
            disabled={readOnly}
            onChange={(e) => {
              if (readOnly) return;
              const v = e.target.value;
              onInputChange(v);
              if (v.includes(",")) onAdd(v);
            }}
            onKeyDown={(e) => {
              if (readOnly) return;
              const isEnter = e.key === "Enter" || (e as React.KeyboardEvent<HTMLInputElement>).keyCode === 13;
              if (isEnter || e.key === ",") {
                e.preventDefault();
                onAdd(inputValue);
              }
            }}
            placeholder={placeholder}
            className={useProfileChips ? profileInputClass : `flex-1 min-w-0 border-0 bg-transparent py-2 text-[15px] font-medium text-foreground focus:outline-none focus:ring-0 ${isPill ? "placeholder:text-muted-foreground" : "placeholder:text-muted-foreground"}`}
          />
        </form>
        {helperText && (
          <p className={useProfileChips ? profileHelperClass : isPill ? "text-[13px] text-muted-foreground mt-1.5" : "text-[10px] text-muted-foreground/50 mt-0.5 truncate"}>{helperText}</p>
        )}
      </div>
    );
  }

  return (
    <div className={spaceClass}>
      <Label htmlFor={inputId} className="text-typo-muted font-medium">
        {label}
      </Label>
      <p className="text-typo-caption text-muted-foreground">
        Нажмите на чип для редактирования, крестик — удалить
      </p>
      <div className={`flex flex-wrap gap-2 ${chipsMb}`}>
        {items.map((item, i) => (
          <Badge
            key={i}
            variant="secondary"
            className="cursor-pointer gap-1 pr-1"
            onClick={() => onEdit(item, i)}
          >
            {item}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(i);
              }}
              className="rounded-full p-0.5 hover:bg-muted"
              aria-label="Удалить"
            >
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
      </div>
      <form
        className="flex gap-2 flex-1 min-w-0"
        onSubmit={(e) => {
          e.preventDefault();
          if (!readOnly && inputValue.trim()) onAdd(inputValue);
        }}
        noValidate
      >
        <Input
          id={inputId}
          type="text"
          autoComplete="off"
          value={inputValue}
          readOnly={readOnly}
          disabled={readOnly}
          onChange={(e) => {
            if (readOnly) return;
            const v = e.target.value;
            onInputChange(v);
            if (v.includes(",")) onAdd(v);
          }}
          onKeyDown={(e) => {
            if (readOnly) return;
            const isEnter = e.key === "Enter" || (e as React.KeyboardEvent<HTMLInputElement>).keyCode === 13;
            if (isEnter || e.key === ",") {
              e.preventDefault();
              onAdd(inputValue);
            }
          }}
          placeholder={placeholder}
          className="h-11 border-2 flex-1"
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-11 w-11 shrink-0"
          onClick={() => onAdd(inputValue)}
          disabled={readOnly || !inputValue.trim()}
          aria-label="Добавить"
        >
          <Plus className="w-5 h-5" />
        </Button>
      </form>
    </div>
  );
}
